/**
 * useBoardFilters — wraps usePersistedFilters with board-specific defaults.
 *
 * Type constraints (epicFilter/storyFilter) are applied via the sanitize function
 * on restore, NOT via a useEffect. This eliminates the render loop that the old
 * BoardPage had from its type-filter sync effect.
 */

import { useMemo } from 'react';
import { usePersistedFilters } from './usePersistedFilters';
import type { FilterState } from '../components/work/shared/FilterBar';

const DEFAULT_BOARD_FILTERS: FilterState = {
  searchText: '',
  types: [],
  priorities: [],
  initiatives: [],
  teams: [],
  assignees: [],
  labels: [],
  showMyIssues: false,
  showUnassigned: false,
  startDateRange: undefined,
  dueDateRange: undefined,
};

interface UseBoardFiltersOptions {
  epicFilter?: boolean;
  storyFilter?: boolean;
}

function createSanitizer(options: UseBoardFiltersOptions) {
  return (state: FilterState): FilterState => {
    // Enforce route-driven type constraints
    const types: string[] = [];
    if (options.epicFilter) types.push('epic');
    if (options.storyFilter) types.push('story');
    if (types.length > 0) state.types = types;

    // Rehydrate date ranges from ISO strings (localStorage stores them as strings)
    if (state.startDateRange) {
      state.startDateRange = {
        from: new Date(state.startDateRange.from),
        to: new Date(state.startDateRange.to),
      };
    }
    if (state.dueDateRange) {
      state.dueDateRange = {
        from: new Date(state.dueDateRange.from),
        to: new Date(state.dueDateRange.to),
      };
    }
    return state;
  };
}

export function useBoardFilters(
  projectId: string | undefined,
  boardId: string,
  options: UseBoardFiltersOptions = {},
) {
  const enabled = !!projectId && !!boardId;

  // Memoize sanitizer so usePersistedFilters doesn't see a new ref every render
  const sanitize = useMemo(
    () => createSanitizer(options),
    [options.epicFilter, options.storyFilter],
  );

  return usePersistedFilters<FilterState>({
    scopeKey: enabled ? `board.${projectId}.${boardId}` : '',
    defaultState: DEFAULT_BOARD_FILTERS,
    sanitize,
    enabled,
  });
}
